import BlockApi from '@/api/api';
import { useConfig } from '@/hooks/useConfig';
import { BackendApplication, BackendApplicationRevision } from '@/types/backend';
import { formatDate } from '@/util/time';
import { Box, Breadcrumbs, CardContent, Divider, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';

type ApplicationRevisionsProps = {
  application?: BackendApplication;
  selectedRevision: BackendApplicationRevision | undefined;
  setSelectedRevision: (newRevision: BackendApplicationRevision | undefined) => void;
  confirmRevision: (revision: number) => void;
};

const ApplicationRevisions: FC<ApplicationRevisionsProps> = ({
  application,
  selectedRevision,
  setSelectedRevision,
  confirmRevision
}) => {
  const { config } = useConfig();
  const auth = useAuth();

  const [revisions, setRevisions] = useState<BackendApplicationRevision[]>([]);

  const handleConfirm = (backendRevision?: BackendApplicationRevision) => {
    const revision = backendRevision ?? selectedRevision;
    revision && confirmRevision(revision.revision);
  };

  const getHistory = async () => {
    if (!auth?.user || !application?.applicationId) return;

    const list = await BlockApi(config.apiUrl, auth.user.access_token).application.history(application.applicationId);

    if (!list) return;

    setRevisions(list);
  };

  useEffect(() => {
    setSelectedRevision(undefined);
    getHistory();
  }, [application]);

  return (
    <CardContent sx={{ width: '100%', pt: 0 }}>
      <Typography variant='h5' color='text.secondary' mb={0.5}>
        {application?.label || ''}
      </Typography>
      <Breadcrumbs>
        <Typography color='text.secondary' fontFamily='monospace' fontSize='0.75rem'>
          {application?.name}
        </Typography>
      </Breadcrumbs>
      <Divider />
      <List dense disablePadding sx={{ flex: 1, overflowY: 'auto', maxHeight: 450 }}>
        {revisions.map((revision) => {
          const isSelected = selectedRevision?.revision === revision.revision;
          return (
            <ListItemButton
              key={revision.revision}
              selected={isSelected}
              ref={isSelected ? (el) => el?.scrollIntoView({ block: 'nearest' }) : null}
              onClick={() => setSelectedRevision(revision)}
              onDoubleClick={() => {
                setSelectedRevision(revision);
                handleConfirm(revision);
              }}
            >
              <ListItemText
                primary={
                  <Box display='flex' justifyContent='space-between' alignItems='baseline' gap={1}>
                    <Typography variant='body2' fontWeight={500}>
                      Revision {revision.revision}
                    </Typography>
                    <Typography variant='caption' color='text.secondary' noWrap>
                      {formatDate(revision.created)}
                    </Typography>
                  </Box>
                }
              />
            </ListItemButton>
          );
        })}
      </List>
    </CardContent>
  );
};

export default ApplicationRevisions;
